<template>
  <b-modal
    id="payload-report-guide-modal"
    v-model="showModal"
    size="lg"
    scrollable
    centered
    hide-footer
    header-class="border-bottom-0 pb-0"
    body-class="pt-1 px-1 px-md-2 pb-2"
  >
    <!-- Modal Header -->
    <template #modal-header="{ close }">
      <div class="d-flex align-items-center justify-content-between w-100 pb-50 border-bottom">
        <div class="d-flex align-items-center">
          <b-avatar
            variant="light-primary"
            size="46px"
            class="mr-1 shadow-sm font-weight-bolder flex-shrink-0"
          >
            <feather-icon
              icon="BookOpenIcon"
              size="22"
              class="text-primary"
            />
          </b-avatar>
          <div>
            <div
              class="d-flex align-items-center flex-wrap"
              style="gap: 6px;"
            >
              <h5 class="mb-0 font-weight-bolder guide-title">
                {{ $t('How to Interpret this Report') }}
              </h5>
              <b-badge
                variant="light-primary"
                pill
                class="font-small-1"
              >
                {{ $t('Operational Guide') }}
              </b-badge>
            </div>
            <small class="guide-subtitle d-block mt-25">
              {{ $t('See methodology and indicators') }}
            </small>
          </div>
        </div>

        <b-button
          variant="flat-secondary"
          size="sm"
          class="btn-icon rounded-circle"
          @click="close"
        >
          <feather-icon
            icon="XIcon"
            size="18"
          />
        </b-button>
      </div>
    </template>

    <!-- Modal Body Content -->
    <div class="payload-guide-content">
      <!-- Tabs Navigation -->
      <b-tabs
        v-model="activeTab"
        pills
        nav-class="mb-1"
        content-class="mt-1"
      >
        <!-- TAB 1: O QUE É UM CICLO -->
        <b-tab active>
          <template #title>
            <feather-icon
              icon="TruckIcon"
              size="14"
              class="mr-50"
            />
            <span>{{ $t('What is a Cycle?') }}</span>
          </template>

          <div class="tab-cycle-guide">
            <!-- Hero Definition Card -->
            <div class="p-1 rounded bg-light-primary border border-primary mb-1">
              <h6 class="font-weight-bolder text-primary mb-50 d-flex align-items-center">
                <feather-icon
                  icon="CheckCircleIcon"
                  size="16"
                  class="mr-50"
                />
                {{ $t('Transport Cycle Concept') }}
              </h6>
              <p class="font-small-3 mb-0 guide-desc">
                {{ $t('A cycle is a') }} <strong>{{ $t('complete trip') }}</strong>: {{ $t('the truck loads, travels with payload, and unloads. The') }} <strong>{{ $t('travel weight') }}</strong> {{ $t('is the stabilized load in this transport section.') }}
              </p>
            </div>

            <!-- Visual Flowchart of Cycle -->
            <b-card
              no-body
              class="p-1 mb-1 border shadow-none bg-light-secondary"
            >
              <h6 class="font-weight-bolder font-small-2 guide-card-label text-uppercase mb-75">
                {{ $t('Stages of a Complete Journey') }}
              </h6>

              <b-row class="text-center font-small-2">
                <!-- Step 1: Carregamento -->
                <b-col
                  cols="12"
                  sm="4"
                  class="mb-75 mb-sm-0"
                >
                  <div class="p-75 rounded bg-white border border-warning h-100 shadow-xs">
                    <b-avatar
                      variant="light-warning"
                      size="34px"
                      class="mb-50"
                    >
                      <feather-icon
                        icon="ArrowDownCircleIcon"
                        size="18"
                      />
                    </b-avatar>
                    <strong class="d-block text-warning font-weight-bolder">{{ $t('1. Loading') }}</strong>
                    <span class="guide-step-desc font-small-1">
                      {{ $t('The load rises until it stabilizes.') }}
                    </span>
                  </div>
                </b-col>

                <!-- Step 2: Transporte Estável -->
                <b-col
                  cols="12"
                  sm="4"
                  class="mb-75 mb-sm-0"
                >
                  <div class="p-75 rounded bg-white border border-primary h-100 shadow-xs">
                    <b-avatar
                      variant="light-primary"
                      size="34px"
                      class="mb-50"
                    >
                      <feather-icon
                        icon="TruckIcon"
                        size="18"
                      />
                    </b-avatar>
                    <strong class="d-block text-primary font-weight-bolder">{{ $t('2. Stable Transport') }}</strong>
                    <span class="guide-step-desc font-small-1">
                      {{ $t('Section in transit with stable load. This is where weight is measured.') }}
                    </span>
                  </div>
                </b-col>

                <!-- Step 3: Descarga -->
                <b-col
                  cols="12"
                  sm="4"
                >
                  <div class="p-75 rounded bg-white border border-success h-100 shadow-xs">
                    <b-avatar
                      variant="light-success"
                      size="34px"
                      class="mb-50"
                    >
                      <feather-icon
                        icon="ArrowUpCircleIcon"
                        size="18"
                      />
                    </b-avatar>
                    <strong class="d-block text-success font-weight-bolder">{{ $t('3. Unloading') }}</strong>
                    <span class="guide-step-desc font-small-1">
                      {{ $t('Tilting and weight relief.') }}
                    </span>
                  </div>
                </b-col>
              </b-row>
            </b-card>

            <!-- Grid: O que entra vs O que fica de fora -->
            <b-row class="mb-1">
              <!-- What is excluded -->
              <b-col
                cols="12"
                md="6"
                class="mb-1 mb-md-0"
              >
                <div class="p-1 rounded bg-light-danger border border-danger h-100">
                  <h6 class="font-weight-bolder text-danger mb-50 d-flex align-items-center">
                    <feather-icon
                      icon="XCircleIcon"
                      size="16"
                      class="mr-50"
                    />
                    {{ $t('What is left out of the weight?') }}
                  </h6>
                  <ul class="font-small-2 mb-0 pl-1 guide-desc">
                    <li class="mb-25">
                      <strong>{{ $t('Dynamic peaks:') }}</strong> {{ $t('pothole jolts on the track, sudden oscillations and braking.') }}
                    </li>
                    <li class="mb-25">
                      <strong>{{ $t('Uncalibrated moving scale:') }}</strong> {{ $t('suspension oscillations on uneven terrain.') }}
                    </li>
                    <li>
                      <strong>{{ $t('Weight transitions:') }}</strong> {{ $t('the up ramp for loading and the down ramp for unloading.') }}
                    </li>
                  </ul>
                </div>
              </b-col>

              <!-- Validation Criteria -->
              <b-col
                cols="12"
                md="6"
              >
                <div class="p-1 rounded bg-light-info border border-info h-100">
                  <h6 class="font-weight-bolder text-info mb-50 d-flex align-items-center">
                    <feather-icon
                      icon="FilterIcon"
                      size="16"
                      class="mr-50"
                    />
                    {{ $t('When does the trip come on the list?') }}
                  </h6>
                  <p class="font-small-2 mb-50 guide-desc">
                    {{ $t('The trip is only considered a valid cycle when:') }}
                  </p>
                  <ul class="font-small-2 mb-0 pl-1 guide-desc">
                    <li class="mb-25">
                      {{ $t('It lasts long enough to prove real operational travel.') }}
                    </li>
                    <li class="mb-25">
                      {{ $t('It has sufficient and reliable telemetry readings.') }}
                    </li>
                    <li>
                      {{ $t('The load is real (quick jolts or short maneuvers in the yard do not become a cycle).') }}
                    </li>
                  </ul>
                </div>
              </b-col>
            </b-row>

            <!-- Incomplete Cycle Alert Box -->
            <div class="p-1 rounded bg-light-warning border border-warning">
              <div class="d-flex align-items-start">
                <feather-icon
                  icon="AlertTriangleIcon"
                  size="18"
                  class="text-warning mr-50 mt-25 flex-shrink-0"
                />
                <div>
                  <h6 class="font-weight-bolder text-warning mb-25">
                    {{ $t('Why is a cycle marked as "Incomplete"?') }}
                  </h6>
                  <p class="font-small-2 mb-0 guide-desc">
                    {{ $t('The cycle receives the alert') }} <strong>{{ $t('Incomplete') }}</strong> {{ $t('when:') }}
                  </p>
                  <ul class="font-small-2 mb-0 pl-1 mt-25 guide-desc">
                    <li>{{ $t('The start or end period filter cut the trip in half;') }}</li>
                    <li>{{ $t('There was a prolonged communication/telemetry gap without any readings during the journey;') }}</li>
                    <li>{{ $t('The unloading stage was not identified at the destination.') }}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </b-tab>

        <!-- TAB 2: MÉDIA E MEDIANA -->
        <b-tab>
          <template #title>
            <feather-icon
              icon="TrendingUpIcon"
              size="14"
              class="mr-50"
            />
            <span>{{ $t('Mean & Median') }}</span>
          </template>

          <div class="tab-media-mediana">
            <p class="guide-intro font-small-3 mb-1">
              {{ $t('Understand why we analyze both the mean and the median to evaluate the consistency of the operation:') }}
            </p>

            <b-row class="mb-1">
              <!-- Média -->
              <b-col
                cols="12"
                md="6"
                class="mb-1 mb-md-0"
              >
                <div class="p-1 rounded border bg-light-secondary h-100">
                  <div class="d-flex align-items-center mb-50">
                    <b-avatar
                      variant="light-primary"
                      size="34px"
                      class="mr-50"
                    >
                      <feather-icon
                        icon="DivideIcon"
                        size="16"
                      />
                    </b-avatar>
                    <div>
                      <h6 class="mb-0 font-weight-bolder text-primary">
                        {{ $t('Average Load') }}
                      </h6>
                      <small class="guide-formula font-small-1 font-weight-bold">{{ $t('Sum of loads ÷ Number of cycles') }}</small>
                    </div>
                  </div>
                  <p class="font-small-2 guide-desc mb-0">
                    {{ $t('Add up all charges and divide by the number of trips.') }}
                    <br>
                    <strong>{{ $t('Attention to behavior:') }}</strong> {{ $t('A single very light or very heavy trip pulls the average up or down.') }}
                  </p>
                </div>
              </b-col>

              <!-- Mediana -->
              <b-col
                cols="12"
                md="6"
              >
                <div class="p-1 rounded border bg-light-success border-success h-100">
                  <div class="d-flex align-items-center mb-50">
                    <b-avatar
                      variant="light-success"
                      size="34px"
                      class="mr-50"
                    >
                      <feather-icon
                        icon="TargetIcon"
                        size="16"
                      />
                    </b-avatar>
                    <div>
                      <h6 class="mb-0 font-weight-bolder text-success">
                        {{ $t('Median Load') }}
                      </h6>
                      <small class="guide-formula font-small-1 font-weight-bold">{{ $t('The core value of the operation') }}</small>
                    </div>
                  </div>
                  <p class="font-small-2 guide-desc mb-0">
                    {{ $t('It is the load of the') }} <strong>{{ $t('middle trip') }}</strong>{{ $t(', placing all cycles in order of weight. It shows the') }} <strong>{{ $t('real typical trip') }}</strong>{{ $t('. When the quantity is even, it is the average between the two central trips.') }}
                  </p>
                </div>
              </b-col>
            </b-row>

            <!-- Interpretação Prática: Como comparar -->
            <div class="p-1 rounded bg-light-info border border-info mb-1">
              <h6 class="font-weight-bolder text-info mb-50 d-flex align-items-center">
                <feather-icon
                  icon="SlidersIcon"
                  size="16"
                  class="mr-50"
                />
                {{ $t('How to interpret the relationship between Mean and Median:') }}
              </h6>
              <div
                class="d-flex flex-column font-small-2 guide-desc"
                style="gap: 8px;"
              >
                <div class="d-flex align-items-start">
                  <b-badge
                    variant="success"
                    pill
                    class="mr-75 mt-25 font-small-1"
                  >
                    {{ $t('Close Values') }}
                  </b-badge>
                  <span>
                    <strong>{{ $t('Near Average and Median:') }}</strong> {{ $t('They indicate that the operation is standardized and the loads for each trip are very similar to each other.') }}
                  </span>
                </div>
                <div class="d-flex align-items-start">
                  <b-badge
                    variant="warning"
                    pill
                    class="mr-75 mt-25 font-small-1"
                  >
                    {{ $t('Divergent Values') }}
                  </b-badge>
                  <span>
                    <strong>{{ $t('Average very different from the Median:') }}</strong> {{ $t('It reveals that some atypical cycles (empty trips, with soft ground or extreme excess) are distorting the average.') }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Variabilidade Card -->
            <div class="p-1 rounded border bg-white shadow-xs">
              <div class="d-flex align-items-center justify-content-between flex-wrap">
                <div class="d-flex align-items-center mb-25 mb-sm-0">
                  <feather-icon
                    icon="ActivityIcon"
                    size="18"
                    class="text-primary mr-50"
                  />
                  <div>
                    <h6 class="mb-0 font-weight-bolder guide-title">
                      {{ $t('Variability (%)') }}
                    </h6>
                    <small class="guide-formula font-small-1 font-weight-bold">{{ $t('Statistical dispersion of loads') }}</small>
                  </div>
                </div>
                <div class="font-small-2 guide-desc">
                  {{ $t('Represents') }} <strong>{{ $t('how much the loads deviate from the average') }}</strong>.
                </div>
              </div>
              <p class="font-small-2 guide-desc mb-0 mt-50 border-top pt-50">
                • <strong>{{ $t('Low value:') }}</strong> {{ $t('It means homogeneous loads and consistency in loading.') }}
                <br>
                • <strong>{{ $t('High value:') }}</strong> {{ $t('It signals irregular operation, with large fluctuations in weight between trips.') }}
              </p>
            </div>
          </div>
        </b-tab>

        <!-- TAB 3: GLOSSÁRIO DE INDICADORES -->
        <b-tab>
          <template #title>
            <feather-icon
              icon="ListIcon"
              size="14"
              class="mr-50"
            />
            <span>{{ $t('Glossary of Indicators') }}</span>
          </template>

          <div class="tab-indicadores-guide">
            <p class="guide-intro font-small-3 mb-1">
              {{ $t('All indicators in the report explained in a simple and direct way:') }}
            </p>

            <div
              class="indicators-grid d-flex flex-column"
              style="gap: 8px;"
            >
              <!-- 1. Total Transportado -->
              <div class="p-75 rounded border bg-light-secondary d-flex align-items-start">
                <feather-icon
                  icon="PackageIcon"
                  size="18"
                  class="text-primary mr-75 mt-25 flex-shrink-0"
                />
                <div>
                  <strong class="guide-item-title font-small-2 d-block">{{ $t('Total Transported (t)') }}</strong>
                  <span class="guide-desc font-small-2">
                    {{ $t('Sum of loads from all valid cycles considered in the period.') }}
                  </span>
                </div>
              </div>

              <!-- 2. Ciclos -->
              <div class="p-75 rounded border bg-light-secondary d-flex align-items-start">
                <feather-icon
                  icon="RepeatIcon"
                  size="18"
                  class="text-info mr-75 mt-25 flex-shrink-0"
                />
                <div>
                  <strong class="guide-item-title font-small-2 d-block">{{ $t('Cycles (Trips)') }}</strong>
                  <span class="guide-desc font-small-2">
                    {{ $t('Total number of completed trips made by the vehicle in the filtered period.') }}
                  </span>
                </div>
              </div>

              <!-- 3. Conformidade -->
              <div class="p-75 rounded border bg-light-success d-flex align-items-start">
                <feather-icon
                  icon="AwardIcon"
                  size="18"
                  class="text-success mr-75 mt-25 flex-shrink-0"
                />
                <div>
                  <div class="d-flex align-items-center">
                    <strong class="text-success font-small-2 mr-50">{{ $t('Compliance (%)') }}</strong>
                    <b-badge
                      variant="light-success"
                      pill
                      class="font-small-1"
                    >
                      {{ $t('90% to 110%') }}
                    </b-badge>
                  </div>
                  <span class="guide-desc font-small-2 d-block mt-25">
                    {{ $t('Percentage of cycles in the ideal range (between 90% and 110% of the truck\'s nominal capacity). It only appears when the nominal capacity has been entered.') }}
                  </span>
                </div>
              </div>

              <!-- 4. Perda de Capacidade -->
              <div class="p-75 rounded border bg-light-warning d-flex align-items-start">
                <feather-icon
                  icon="ArrowDownIcon"
                  size="18"
                  class="text-warning mr-75 mt-25 flex-shrink-0"
                />
                <div>
                  <strong class="text-warning font-small-2 d-block">{{ $t('Loss of Capacity (t)') }}</strong>
                  <span class="guide-desc font-small-2">
                    {{ $t('Tons remaining to reach nominal capacity, added together across all cycles.') }}
                    <strong>{{ $t('Important:') }}</strong> {{ $t('a trip above capacity does not compensate for another that operated below.') }}
                  </span>
                </div>
              </div>

              <!-- 5. Excesso acima do Ideal -->
              <div class="p-75 rounded border bg-light-danger d-flex align-items-start">
                <feather-icon
                  icon="AlertOctagonIcon"
                  size="18"
                  class="text-danger mr-75 mt-25 flex-shrink-0"
                />
                <div>
                  <strong class="text-danger font-small-2 d-block">{{ $t('Excess above Ideal (t)') }}</strong>
                  <span class="guide-desc font-small-2">
                    {{ $t('Tons loaded above the ceiling of 110% of nominal capacity, added together. It represents excess weight that generates premature wear and risk of tipping over.') }}
                  </span>
                </div>
              </div>

              <!-- 6. Tempos: Duração, Retorno e Intervalo -->
              <div class="p-75 rounded border bg-light-secondary d-flex align-items-start">
                <feather-icon
                  icon="ClockIcon"
                  size="18"
                  class="text-primary mr-75 mt-25 flex-shrink-0"
                />
                <div>
                  <strong class="guide-item-title font-small-2 d-block">{{ $t('Duration, Empty Return and Average Interval') }}</strong>
                  <ul class="font-small-2 guide-desc mb-0 pl-1 mt-25">
                    <li><strong>{{ $t('Average, minimum and maximum duration:') }}</strong> {{ $t('From the beginning of loading to the end of unloading.') }}</li>
                    <li><strong>{{ $t('Average empty return:') }}</strong> {{ $t('Time between the end of one trip and the start of the next.') }}</li>
                    <li><strong>{{ $t('Average interval:') }}</strong> {{ $t('Time between the start of one trip and the start of the next, with travel and waiting included.') }}</li>
                  </ul>
                </div>
              </div>

              <!-- 7. Produtividade de Relógio vs. Efetiva -->
              <div class="p-75 rounded border bg-light-primary d-flex align-items-start">
                <feather-icon
                  icon="ZapIcon"
                  size="18"
                  class="text-primary mr-75 mt-25 flex-shrink-0"
                />
                <div>
                  <strong class="text-primary font-small-2 d-block">{{ $t('Clock Productivity vs. Effective Productivity (t/h)') }}</strong>
                  <ul class="font-small-2 guide-desc mb-0 pl-1 mt-25">
                    <li>
                      <strong>{{ $t('Clock productivity:') }}</strong> {{ $t('Tons per hour calculated from the first trip to the last, with all the waiting and queues in between.') }}
                    </li>
                    <li>
                      <strong>{{ $t('Effective productivity:') }}</strong> {{ $t('Tons per hour considering only the time the equipment was actually working. Long stops are excluded.') }}
                    </li>
                  </ul>
                </div>
              </div>

              <!-- Callout sobre ausência de capacidade -->
              <div class="p-75 rounded bg-light-warning border border-warning font-small-2 guide-desc mt-25">
                <feather-icon
                  icon="InfoIcon"
                  size="14"
                  class="text-warning mr-25"
                />
                <strong>{{ $t('Without the truck capacity stated:') }}</strong> {{ $t('The report presents the weight in tons normally, but omits compliance, loss of capacity, excess and classification by bands.') }}
              </div>
            </div>
          </div>
        </b-tab>

        <!-- TAB 4: PRAÇAS OPERACIONAIS -->
        <b-tab>
          <template #title>
            <feather-icon
              icon="MapPinIcon"
              size="14"
              class="mr-50"
            />
            <span>{{ $t('Operational Yards') }}</span>
          </template>

          <div class="tab-pracas-guide">
            <!-- Hero Definition Card -->
            <div class="p-1 rounded bg-light-primary border border-primary mb-1">
              <h6 class="font-weight-bolder text-primary mb-50 d-flex align-items-center">
                <feather-icon
                  icon="MapPinIcon"
                  size="16"
                  class="mr-50"
                />
                {{ $t('How Operational Yards are Identified') }}
              </h6>
              <p class="font-small-3 mb-0 guide-desc">
                {{ $t('An') }} <strong>{{ $t('Operational Yard') }}</strong> {{ $t('represents a consolidated geographic area that brings together transport cycles with loading or unloading points in the same region.') }}
              </p>
            </div>

            <!-- Tópicos Estruturados em Cards / Grid -->
            <b-row class="mb-1">
              <!-- Captura dos Pontos -->
              <b-col
                cols="12"
                md="6"
                class="mb-1 mb-md-0"
              >
                <div class="p-1 rounded border bg-light-secondary h-100 shadow-xs">
                  <div class="d-flex align-items-center mb-50">
                    <b-avatar
                      variant="light-primary"
                      size="32px"
                      class="mr-50 flex-shrink-0"
                    >
                      <feather-icon
                        icon="NavigationIcon"
                        size="16"
                        class="text-primary"
                      />
                    </b-avatar>
                    <div>
                      <h6 class="font-weight-bolder mb-0 guide-item-title font-small-3">
                        {{ $t('Capturing Cycle Points') }}
                      </h6>
                      <small class="text-muted">{{ $t('Origin and destination for each trip') }}</small>
                    </div>
                  </div>
                  <p class="font-small-2 guide-desc mb-50">
                    {{ $t('At each completed cycle, the system marks two spatial landmarks on the map:') }}
                  </p>
                  <ul class="font-small-2 guide-desc pl-1 mb-0">
                    <li class="mb-25">
                      <strong>{{ $t('Loading Point:') }}</strong> {{ $t('exact location where the vehicle completed loading and the weight was stabilized.') }}
                    </li>
                    <li>
                      <strong>{{ $t('Unloading Point:') }}</strong> {{ $t('last coordinate with cargo recorded before unloading began.') }}
                    </li>
                  </ul>
                </div>
              </b-col>

              <!-- Agrupamento Inteligente (Raio 150m) -->
              <b-col
                cols="12"
                md="6"
              >
                <div class="p-1 rounded border bg-light-secondary h-100 shadow-xs">
                  <div class="d-flex align-items-center mb-50">
                    <b-avatar
                      variant="light-info"
                      size="32px"
                      class="mr-50 flex-shrink-0"
                    >
                      <feather-icon
                        icon="TargetIcon"
                        size="16"
                        class="text-info"
                      />
                    </b-avatar>
                    <div>
                      <h6 class="font-weight-bolder mb-0 guide-item-title font-small-3">
                        {{ $t('Smart Grouping (150m Radius)') }}
                      </h6>
                      <small class="text-muted">{{ $t('Proximity clustering') }}</small>
                    </div>
                  </div>
                  <p class="font-small-2 guide-desc mb-50">
                    {{ $t('Coordinates recorded within a proximity of') }} <strong>{{ $t('150 meters') }}</strong> {{ $t('among themselves are consolidated in the same yard.') }}
                  </p>
                  <ul class="font-small-2 guide-desc pl-1 mb-0">
                    <li class="mb-25">
                      {{ $t('The pin on the map is positioned on the') }} <strong>{{ $t('geographic center (centroid)') }}</strong> {{ $t('of all points in the group.') }}
                    </li>
                    <li>
                      {{ $t('Work fronts farther than 150m remain separated into distinct yards.') }}
                    </li>
                  </ul>
                </div>
              </b-col>
            </b-row>

            <b-row class="mb-1">
              <!-- Segregação de Operações -->
              <b-col
                cols="12"
                md="6"
                class="mb-1 mb-md-0"
              >
                <div class="p-1 rounded border bg-light-secondary h-100 shadow-xs">
                  <div class="d-flex align-items-center mb-50">
                    <b-avatar
                      variant="light-warning"
                      size="32px"
                      class="mr-50 flex-shrink-0"
                    >
                      <feather-icon
                        icon="LayersIcon"
                        size="16"
                        class="text-warning"
                      />
                    </b-avatar>
                    <div>
                      <h6 class="font-weight-bolder mb-0 guide-item-title font-small-3">
                        {{ $t('Segregation of Operations') }}
                      </h6>
                      <small class="text-muted">{{ $t('Independent loading and unloading') }}</small>
                    </div>
                  </div>
                  <p class="font-small-2 guide-desc mb-0">
                    {{ $t('Loading and unloading areas do not mix. If the same physical yard is used for loading and unloading, the system will display') }} <strong>{{ $t('two distinct yards') }}</strong> {{ $t('on the map (blue for loading and orange for unloading).') }}
                  </p>
                </div>
              </b-col>

              <!-- Indicadores Consolidados -->
              <b-col
                cols="12"
                md="6"
              >
                <div class="p-1 rounded border bg-light-secondary h-100 shadow-xs">
                  <div class="d-flex align-items-center mb-50">
                    <b-avatar
                      variant="light-success"
                      size="32px"
                      class="mr-50 flex-shrink-0"
                    >
                      <feather-icon
                        icon="BarChart2Icon"
                        size="16"
                        class="text-success"
                      />
                    </b-avatar>
                    <div>
                      <h6 class="font-weight-bolder mb-0 guide-item-title font-small-3">
                        {{ $t('Operational Yard Indicators') }}
                      </h6>
                      <small class="text-muted">{{ $t('Consolidated metrics when clicking on marker') }}</small>
                    </div>
                  </div>
                  <ul class="font-small-2 guide-desc pl-1 mb-0">
                    <li class="mb-25">
                      <strong>{{ $t('Total Cycles:') }}</strong> {{ $t('volume of trips processed at the yard.') }}
                    </li>
                    <li class="mb-25">
                      <strong>{{ $t('Average Load (t):') }}</strong> {{ $t('average weight transported by trucks.') }}
                    </li>
                    <li>
                      <strong>{{ $t('Compliance Index (%):') }}</strong> {{ $t('percentage of trips in the ideal range (90% to 110% of the reported nominal capacity).') }}
                    </li>
                  </ul>
                </div>
              </b-col>
            </b-row>

            <!-- Critério de Confiabilidade GPS -->
            <div class="p-1 rounded bg-light-secondary border shadow-xs">
              <div class="d-flex align-items-start">
                <feather-icon
                  icon="ShieldOffIcon"
                  size="20"
                  class="text-danger mr-75 mt-25 flex-shrink-0"
                />
                <div>
                  <h6 class="font-weight-bolder mb-25 guide-item-title text-dark">
                    {{ $t('Positioning Reliability Criterion (GPS)') }}
                  </h6>
                  <p class="font-small-2 guide-desc mb-0">
                    {{ $t('Trips with') }} <strong>{{ $t('loss of GPS signal') }}</strong> {{ $t('or inconsistent telemetry') }} <strong>{{ $t('do not enter any yard') }}</strong>{{ $t('. This criterion guarantees the geographic precision of the centroids and avoids distorted data on the map.') }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </b-tab>
      </b-tabs>
    </div>

    <!-- Modal Footer Actions -->
    <div class="d-flex align-items-center justify-content-between pt-1 mt-1 border-top">
      <small class="guide-footer-note font-small-1">
        {{ $t('Operational questions? Consult the telemetry and fleet management team.') }}
      </small>
      <b-button
        variant="primary"
        size="sm"
        @click="showModal = false"
      >
        {{ $t('Got it, Close Guide') }}
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import { computed } from '@vue/composition-api'
import {
  BModal,
  BButton,
  BAvatar,
  BBadge,
  BTabs,
  BTab,
  BRow,
  BCol,
  BCard,
} from 'bootstrap-vue'

export default {
  name: 'PayloadReportGuideModal',
  components: {
    BModal,
    BButton,
    BAvatar,
    BBadge,
    BTabs,
    BTab,
    BRow,
    BCol,
    BCard,
  },
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    initialTab: {
      type: Number,
      default: 0,
    },
  },
  setup(props, { emit }) {
    const showModal = computed({
      get: () => props.value,
      set: val => emit('input', val),
    })

    const activeTab = computed({
      get: () => props.initialTab,
      set: val => emit('update:initialTab', val),
    })

    return {
      showModal,
      activeTab,
    }
  },
}
</script>

<style lang="scss">
#payload-report-guide-modal {
  .modal-dialog {
    max-width: 820px;
  }

  .nav-pills .nav-link {
    font-size: 0.88rem;
    font-weight: 600;
    padding: 0.45rem 0.85rem;
    color: #4b5262;

    &:hover:not(.active) {
      background-color: rgba(115, 103, 240, 0.08);
      color: #5b4de0;
    }

    &.active {
      background-color: #7367f0 !important;
      color: #ffffff !important;
    }
  }

  // --- Tipografia de alto contraste para o tema claro ---
  .guide-title {
    color: #171a22 !important;
  }

  .guide-subtitle {
    color: #4a5260 !important;
    font-weight: 500;
  }

  .guide-footer-note {
    color: #4a5260 !important;
    font-weight: 500;
  }

  .payload-guide-content {
    color: #242934;

    .guide-intro {
      color: #2c3240 !important;
      font-weight: 500;
      line-height: 1.5;
    }

    .guide-desc {
      color: #242934 !important;
      line-height: 1.55;

      strong {
        color: #11141a !important;
        font-weight: 700;
      }
    }

    .guide-step-desc {
      color: #374151 !important;
      line-height: 1.45;
      font-weight: 500;
    }

    .guide-card-label {
      color: #2c3340 !important;
      font-weight: 700;
      letter-spacing: 0.5px;
    }

    .guide-item-title {
      color: #171a22 !important;
      font-weight: 700;
    }

    .guide-formula {
      color: #475060 !important;
      font-weight: 600;
    }

    // Fallbacks para classes utilitárias
    .text-dark {
      color: #171a22 !important;
    }

    .text-muted {
      color: #4b5363 !important;
    }

    // --- CARDS COM FUNDO CINZA (bg-light-secondary) ---
    // Remove a regra padrão do Vuexy que forçava cor cinza clara (#82868b) no texto
    .bg-light-secondary {
      background-color: #f1f3f7 !important;
      border-color: #d6dbe4 !important;
      color: #242934 !important;

      .guide-card-label {
        color: #28303f !important;
      }

      .guide-item-title,
      strong,
      h6 {
        color: #14171f !important;
      }

      .guide-desc,
      p,
      span,
      ul,
      li {
        color: #242934 !important;
      }

      .guide-formula,
      small {
        color: #475060 !important;
      }

      .text-muted {
        color: #4b5363 !important;
      }
    }

    // Cards internos brancos
    .bg-white {
      background-color: #ffffff !important;
      border-color: #dbe0ea !important;

      .guide-step-desc,
      .guide-desc {
        color: #333a46 !important;
      }

      strong {
        font-weight: 700;
      }
    }

    // --- CARDS COLORIDOS COM FUNDO CLARO ---
    // Aumenta o contraste dos textos e títulos em relação ao background suave
    .bg-light-primary {
      background-color: rgba(115, 103, 240, 0.09) !important;
      border-color: rgba(115, 103, 240, 0.35) !important;
      color: #242934 !important;

      .text-primary {
        color: #5646eb !important;
      }

      .guide-desc,
      p,
      span,
      ul,
      li {
        color: #222837 !important;
      }
    }

    .bg-light-success {
      background-color: rgba(40, 199, 111, 0.1) !important;
      border-color: rgba(40, 199, 111, 0.38) !important;
      color: #1a281f !important;

      .text-success {
        color: #107c41 !important;
      }

      .guide-desc,
      p,
      span,
      ul,
      li {
        color: #1a281f !important;
      }
    }

    .bg-light-warning {
      background-color: rgba(255, 159, 67, 0.12) !important;
      border-color: rgba(255, 159, 67, 0.42) !important;
      color: #2f2214 !important;

      .text-warning {
        color: #a85800 !important;
      }

      .guide-desc,
      p,
      span,
      ul,
      li {
        color: #2f2214 !important;
      }
    }

    .bg-light-danger {
      background-color: rgba(234, 84, 85, 0.09) !important;
      border-color: rgba(234, 84, 85, 0.38) !important;
      color: #351e20 !important;

      .text-danger {
        color: #c92a2a !important;
      }

      .guide-desc,
      p,
      span,
      ul,
      li {
        color: #351e20 !important;
      }
    }

    .bg-light-info {
      background-color: rgba(0, 207, 232, 0.1) !important;
      border-color: rgba(0, 207, 232, 0.38) !important;
      color: #142730 !important;

      .text-info {
        color: #027a8f !important;
      }

      .guide-desc,
      p,
      span,
      ul,
      li {
        color: #142730 !important;
      }
    }
  }

  // --- MODO ESCURO (dark-layout) ---
  body.dark-layout &,
  .dark-layout & {
    .modal-content {
      background-color: #242b3d !important;
      color: #e2e5eb !important;
    }

    .modal-header,
    .modal-footer,
    .border-bottom,
    .border-top {
      border-color: rgba(255, 255, 255, 0.1) !important;
    }

    .nav-pills .nav-link {
      color: #b4b7bd;

      &:hover:not(.active) {
        background-color: rgba(255, 255, 255, 0.06);
        color: #ffffff;
      }

      &.active {
        background-color: #7367f0 !important;
        color: #ffffff !important;
      }
    }

    .guide-title,
    .guide-item-title,
    h5,
    strong {
      color: #ffffff !important;
    }

    .guide-subtitle,
    .guide-footer-note {
      color: #a4abb9 !important;
    }

    .payload-guide-content {
      color: #d1d5db;

      .guide-intro {
        color: #d5d9e2 !important;
      }

      .guide-desc {
        color: #d1d5db !important;

        strong {
          color: #ffffff !important;
        }
      }

      .guide-step-desc {
        color: #c4c9d4 !important;
      }

      .guide-card-label {
        color: #b5bcc9 !important;
      }

      .guide-formula {
        color: #a4abb9 !important;
      }

      .text-dark {
        color: #ffffff !important;
      }

      .text-muted {
        color: #a4abb9 !important;
      }

      .bg-white {
        background-color: #161d31 !important;
        border-color: rgba(255, 255, 255, 0.12) !important;

        .guide-step-desc,
        .guide-desc {
          color: #cdd1dc !important;
        }
      }

      .bg-light-secondary {
        background-color: #182033 !important;
        border-color: rgba(255, 255, 255, 0.12) !important;
        color: #d8dbe2 !important;

        .guide-item-title,
        strong,
        h6 {
          color: #ffffff !important;
        }

        .guide-desc,
        p,
        span,
        ul,
        li {
          color: #cfd4df !important;
        }

        .guide-formula,
        small {
          color: #a4abb9 !important;
        }

        .guide-card-label {
          color: #b5bcc9 !important;
        }

        .text-muted {
          color: #a4abb9 !important;
        }
      }

      .bg-light-primary {
        background-color: rgba(115, 103, 240, 0.18) !important;
        border-color: rgba(115, 103, 240, 0.45) !important;

        .text-primary {
          color: #9288f8 !important;
        }

        .guide-desc,
        p,
        span,
        ul,
        li {
          color: #e0e2ec !important;
        }
      }

      .bg-light-success {
        background-color: rgba(40, 199, 111, 0.18) !important;
        border-color: rgba(40, 199, 111, 0.45) !important;

        .text-success {
          color: #48d989 !important;
        }

        .guide-desc,
        p,
        span,
        ul,
        li {
          color: #e0e8e3 !important;
        }
      }

      .bg-light-warning {
        background-color: rgba(255, 159, 67, 0.18) !important;
        border-color: rgba(255, 159, 67, 0.45) !important;

        .text-warning {
          color: #ffb56b !important;
        }

        .guide-desc,
        p,
        span,
        ul,
        li {
          color: #eee4d8 !important;
        }
      }

      .bg-light-danger {
        background-color: rgba(234, 84, 85, 0.18) !important;
        border-color: rgba(234, 84, 85, 0.45) !important;

        .text-danger {
          color: #f87171 !important;
        }

        .guide-desc,
        p,
        span,
        ul,
        li {
          color: #eedcdb !important;
        }
      }

      .bg-light-info {
        background-color: rgba(0, 207, 232, 0.18) !important;
        border-color: rgba(0, 207, 232, 0.45) !important;

        .text-info {
          color: #38c5d9 !important;
        }

        .guide-desc,
        p,
        span,
        ul,
        li {
          color: #dbecf0 !important;
        }
      }
    }
  }
}
</style>
